import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  LabelList
} from 'recharts';
import { useAuth } from '../../../../../providers/AuthContext';
import Spinner from '../../../../../components/Spinner';
import { getDataWidget, getWidgetById } from '../../../../../services/apiWidgests';
import { useDashboards } from '../../../../../providers/DashboardContext';
import DatabaseOffIco from '../../../../../components/icons/DatabaseOffIco';
import { connectToProcess } from '../../../../../services/sockets/processSocket';
import { useAppContext } from '../../../../../providers/AppContext';
// 🎨 Generador de colores automáticos según paleta
const generatePaletteColor = (palette, index, total) => {
  const hueStep = 360 / Math.max(total, 1);
  let baseHue;
  switch (palette) {
    case 'red':
      baseHue = 0;
      break;
    case 'blue':
      baseHue = 210;
      break;
    default:
      baseHue = index * hueStep;
      break;
  }

  let saturation, lightness;
  if (palette === 'red') {
    saturation = 70;
    lightness = 55 - (index * 3) % 15;
  } else if (palette === 'blue') {
    saturation = 65;
    lightness = 50 - (index * 3) % 15;
  } else {
    saturation = 60;
    lightness = 70;
  }

  return `hsl(${baseHue + index * 10}, ${saturation}%, ${lightness}%)`;
};

// 🔅 Oscurecer un HSL
const darkenHSL = (hsl, amount = 15) => {
  const [h, s, l] = hsl.replace(/hsl|\(|\)|%/g, '').split(',').map(Number);
  const newLightness = Math.max(l - amount, 0);
  return `hsl(${h}, ${s}%, ${newLightness}%)`;
};

// Tooltip personalizado
const CustomTooltip = ({ active, payload, hidePercent }) => {
  if (active && payload?.length) {
    const labelX = payload[0]?.payload?.label;
    return (
      <div className="bg-black text-white px-2 py-1 rounded opacity-90">
        <div className="font-semibold text-sm mb-1">{labelX}</div>
        {payload.map((p, idx) => {
          if (p.value == null) return null;
          const formattedValue =
            Number(p.value) % 1 === 0 ? Number(p.value) : Number(p.value).toFixed(2);
          return (
            <div key={idx} className="flex items-center space-x-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: p.color }}
              ></span>
              <span className="text-sm">{`${p.name}: ${formattedValue}${!hidePercent ? '%': ""}`}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

// Label encima del punto
const CustomLabel = ({ x, y, value, textColor , hidePercent}) => {
  if (value == null) return null;
  const formattedValue = Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);
  return (
    <text x={x} y={y - 10} fill={textColor} fontSize={12} textAnchor="middle">
      {!hidePercent ? formattedValue + '%': formattedValue }
    </text>
  );
};

const PorcentualRateChartWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { dashboard,selectedDatarange } = useDashboards();

  const [data, setData] = useState([]);
  const [fetchingWidgetData, setFetchingWidgetData] = useState(true);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [widgetTitle, setWidgetTitle] = useState("");
  const [numericLabel, setNumericLabel] = useState("");
  const [timeLabel, setTimeLabel] = useState("");
  const [colorsMap, setColorsMap] = useState({});
  const [selectedLine, setSelectedLine] = useState(null);
  const [graphType, setGraphType] = useState("Line");
  const [hasInterval, setHasInterval] = useState(false);
  const [config, setConfig] = useState({});
  const [initialOrder, setInitialOrder] = useState([]);

  const { addToast } = useAppContext();

  // ⚙️ Obtener info del widget
  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      const conf = response?.configuration || {};
      setConfig(conf);
      setWidgetTitle(response?.title);
      setNumericLabel(
        conf?.valueToCompare?.alias ||
        conf?.valueToCompare?.key ||
        ""
      );
      setTimeLabel(
        conf?.variableToCompare?.alias ||
        conf?.variableToCompare?.key ||
        ""
      );
      setGraphType(conf?.graphType || "Line");
      setHasInterval(!!conf?.hasInterval);
      setFetchingWidgetInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error?.response?.status === 404) console.log("Widget no encontrado");
      else if (error?.response?.status === 500) console.log("Error en el servidor");
      else navigate('/logout');
      setFetchingWidgetInfo(false);
    }
  };

  // 📊 Obtener datos
  const refreshWidgetData = async () => {
    try {
      setFetchingWidgetData(true);
      const response = await getDataWidget(id, {}, auth?.token) || [];
      setData(response);
      setFetchingWidgetData(false);
      if (Array.isArray(response) && response.length > 0 && response[0].group) {
        const groupNames = response.map(g => g.group);
        const distinctColors = groupNames.map((_, idx) =>
          generatePaletteColor(config?.colorPalette || 'pastel', idx, groupNames.length)
        );
        const newColors = {};
        groupNames.forEach((name, idx) => {
          newColors[name] = distinctColors[idx];
        });
        setColorsMap(newColors);
      }
      setFetchingWidgetData(false);
      // Guardar orden inicial
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error?.response?.status === 404) console.log("Widget no encontrado");
      else if (error?.response?.status === 500) console.log("Error en el servidor");
      else navigate('/logout');
      setFetchingWidgetData(false);
    }
  };

  const refreshWidgetDataSilent = async () => {
    try {

      const response = await getDataWidget(id, {}, auth?.token) || [];
      setData(response);

      if (Array.isArray(response) && response.length > 0 && response[0].group) {
        const groupNames = response.map(g => g.group);
        const distinctColors = groupNames.map((_, idx) =>
          generatePaletteColor(config?.colorPalette || 'pastel', idx, groupNames.length)
        );
        const newColors = {};
        groupNames.forEach((name, idx) => {
          newColors[name] = distinctColors[idx];
        });
        setColorsMap(newColors);
      }

    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error?.response?.status === 404) console.log("Widget no encontrado");
      else if (error?.response?.status === 500) console.log("Error en el servidor");
      else navigate('/logout');

    }
  };

  useEffect(() => {
    refreshWidgetInfo();
    refreshWidgetData();
  }, [selectedDatarange, id]);

  useEffect(() => {
    if (!config?.process?.id) return;

    const disconnect = connectToProcess(
      config.process.id,
      process.env.REACT_APP_SOCKET_URL,
      () => {
        // Refresh transparente al recibir update
        refreshWidgetDataSilent();
      }
    );

    return () => disconnect();
  }, [config?.process?.id]);
  // --- Generación de datos ---
  let chartData = [];
  const isGrouped = Array.isArray(data) && data[0]?.group;

  if (hasInterval) {
    if (isGrouped) {
      const labels = [...new Set(data.flatMap(g => g.data.map(d => d.label)))];
      chartData = labels.map(label => {
        const row = { label };
        data.forEach(g => {
          const found = g.data.find(d => d.label === label);
          row[g.group] = found ? Number(found.value ?? 0) : null;
        });
        return row;
      });
    } else {
      chartData = (data || []).map(d => ({
        label: d.label,
        value: Number(d.value ?? 0)
      }));
    }
  } else {
    if (isGrouped) {
      if (data.length === 1 && Array.isArray(data[0].data)) {
        chartData = data[0].data.map(pt => ({
          label: pt.x ?? pt.label,
          value: Number(pt.y ?? pt.value ?? 0)
        }));
      } else {
        chartData = data.map(g => ({
          label: g.group,
          value: Number(g.data?.[0]?.y ?? g.data?.[0]?.value ?? g.value ?? 0)
        }));
      }
    } else {
      chartData = (data || []).map(d => ({
        label: d.label ?? d.x ?? "",
        value: Number(d.value ?? d.y ?? 0)
      }));
    }
  }

  const isBars = graphType === "VBars" || graphType === "HBars";
  const isHorizontal = graphType === "HBars";
  const hasChartPoints = chartData.length > 0;
  const { colorPalette = 'pastel', fixedColor, textColor = '#111827' } = config;

  const handleLegendClick = (o) => {
    const { dataKey } = o;
    setSelectedLine(prev => (prev === dataKey ? null : dataKey));
  };

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded">
      {(fetchingWidgetData  || fetchingWidgetInfo) ? (
        <div className='w-full h-full flex justify-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-center">
            <span className="font-semibold text-gray-600" >
              {widgetTitle} ({numericLabel} vs {timeLabel})
            </span>
          </div>

          {!hasChartPoints ? (
            <div className='w-full h-full flex flex-col items-center justify-center -mt-8'>
              <span className='text-lg text-gray-700'>No hay datos por el momento</span>
              <DatabaseOffIco width='40px' height='40px'  fill='gray'/>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="80%">
              {isBars ? (
                <BarChart
                  layout={isHorizontal ? "vertical" : "horizontal"}
                  data={chartData}
                  margin={{ top: 30, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  {isHorizontal ? (
                    <>
                      <XAxis type="number" stroke={'#6b7280'} />
                      <YAxis type="category" dataKey="label" stroke={'#6b7280'} />
                    </>
                  ) : (
                    <>
                      <XAxis dataKey="label" stroke={'#6b7280'} />
                      <YAxis stroke={'#6b7280'} />
                    </>
                  )}
                  <Tooltip content={<CustomTooltip hidePercent={config?.hidePercent} />} />
                  <Bar dataKey="value" fill={fixedColor || "#3b82f6"} barSize={30} isAnimationActive={false}>
                    <LabelList
                      dataKey="value"
                      position={isHorizontal ? "right" : "top"}
                      fill={textColor}
                      fontSize={12}
                      formatter={(v) => (typeof v === 'number' ? v.toFixed(2) + (!config?.hidePercent ? '%': "") : v)}
                    />
                  </Bar>
                </BarChart>
              ) : (
                <LineChart
                  data={chartData}
                  margin={{ top: 50, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" stroke={'#6b7280'} />
                  <YAxis stroke={'#6b7280'} />
                  <Tooltip content={<CustomTooltip hidePercent={config?.hidePercent} />} />
                  <Legend
                    verticalAlign="top"
                    wrapperStyle={{ top: 0, marginBottom: 10, color: '#6b7280' }}
                    onClick={handleLegendClick}
                  />
                  {hasInterval && isGrouped ? (
                    data.map((group, idx) => {
                      const stroke = colorPalette === 'fixed'
                        ? fixedColor || '#4B5563'
                        : colorsMap[group.group] || generatePaletteColor(colorPalette, idx, data.length);
                      return (
                        <Line
                          key={group.group}
                          type="monotone"
                          dataKey={group.group}
                          name={group.group}
                          stroke={stroke}
                          strokeWidth={2}
                          dot={{ r: 1 }}
                          activeDot={{ r: 6 }}
                          isAnimationActive={false}
                          hide={selectedLine && selectedLine !== group.group}
                        />
                      );
                    })
                  ) : (
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={fixedColor || "#3b82f6"}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                      label={<CustomLabel textColor={textColor} hidePercent={config?.hidePercent} />}
                    />
                  )}
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
};

export default PorcentualRateChartWidget;
